import { Env } from './env';

/**
 * Canonical AppliedBioX marketing-site origins. The portal apps link out to the
 * environment-appropriate one so dev portals never point at production marketing
 * (and vice-versa).
 */
export const PROD_MARKETING_URL = 'https://www.appliedbiox.com';
export const DEV_MARKETING_URL = 'https://dev.appliedbiox.com';

/** Just the bits of {@link Env} the marketing helpers need. */
export type MarketingEnv = Pick<Partial<Env>, 'production' | 'marketingUrl'>;

/**
 * Environment-aware marketing-site base URL, without a trailing slash.
 *
 * Prefers an explicit `env.marketingUrl` (e.g. for a staging origin) and falls
 * back to the `production` flag — guaranteeing a correct link even when an
 * older generated `environment.ts` predates the `marketingUrl` field.
 */
export function marketingBaseUrl(env: MarketingEnv | null | undefined): string {
  const explicit = env?.marketingUrl?.trim();
  if (explicit) {
    return explicit.replace(/\/+$/, '');
  }
  return env?.production ? PROD_MARKETING_URL : DEV_MARKETING_URL;
}

/**
 * Build an absolute marketing URL for a path or hash. A leading slash on
 * `path` is optional; hashes (`#section`) are preserved.
 */
export function marketingLink(env: MarketingEnv | null | undefined, path = ''): string {
  const base = marketingBaseUrl(env);
  if (!path) {
    return base;
  }
  if (path.startsWith('#')) {
    return `${base}/${path}`;
  }
  return `${base}/${path.replace(/^\/+/, '')}`;
}

/**
 * Canonical marketing destinations used across portal chrome (header/footer).
 *
 * Page entries resolve to routes that exist on the marketing site (home, about,
 * partners, contact, blog). Hash entries point at sections of the marketing home
 * (how-it-works, results, science, pricing, faq).
 */
export function marketingLinks(env: MarketingEnv | null | undefined) {
  return {
    home: marketingLink(env),
    about: marketingLink(env, 'about'),
    partners: marketingLink(env, 'partners'),
    contact: marketingLink(env, 'contact'),
    blog: marketingLink(env, 'blog'),
    howItWorks: marketingLink(env, '#how-it-works'),
    results: marketingLink(env, '#results'),
    science: marketingLink(env, '#science'),
    pricing: marketingLink(env, '#pricing'),
    faq: marketingLink(env, '#faq'),
    terms: marketingLink(env, 'terms'),
    privacy: marketingLink(env, 'privacy'),
    cookies: marketingLink(env, 'cookies'),
  } as const;
}

export type MarketingLinks = ReturnType<typeof marketingLinks>;
